/**
 * The desktop rails (left WatchlistPane sidebar, right chat dock) reserved BEFORE FIRST PAINT.
 *
 * 🚨 WHY (register desktop-rails-mount-after-hydration-cls-1102). Both rails are position:fixed, so they move
 * nothing themselves; what moved was <main>'s margin-left / padding-right, which came from CLIENT STATE. The server
 * paint had main full width (x0 w1440), then hydration walked it 0 -> 368 (sidebar), and the lazily mounted dock
 * wrote --alerts-chat-right-pad 0 before restoring its open state and then 368, every step animated by main's 200ms
 * transition. Real-visitor web_vitals: desktop /stocks/<SYM> CLS 0.31-1.45 on 9 of 9 loads, mobile 0.00. Lab, the
 * same harness and minute, values correct at first paint vs control: /pulse 0.641 -> 0.008, /stocks/NVDA
 * 0.690 -> 0.019.
 *
 * ⭐ So a tiny inline <head> script computes both widths from the viewport and the same localStorage keys the two
 * components read, and sets them on <html> before <body> is parsed. <main> takes its insets from those vars in CSS
 * (app/globals.css, main#main[data-rails]). The components then only ever write the SAME value once they have
 * restored their own state, never a 0 placeholder (ConditionalMain waits for SidebarContext `restored`,
 * AlertsChatDock for `openRestored`).
 *
 * ⛔ This module must stay free of 'use client': the root layout (a server component) imports it, and a constant
 * imported from a client module into a server component is a client reference, not a value
 * (feedback_rsc_client_module_helper, guarded by rscClientImports.test.ts).
 */

import { CHAT_DOCK_CHOICE_KEY, CHAT_DOCK_LEGACY_KEY } from '@/lib/chatDockDefault'

export const SIDEBAR_COLLAPSED_STORAGE_KEY = 'dt_sidebar_collapsed'

export const SIDEBAR_WIDTH_EXPANDED_PX = 368
export const SIDEBAR_WIDTH_COLLAPSED_PX = 72
/** Chat panel 360px + 8px right inset. */
export const CHAT_DOCK_PAD_PX = 368

/** Viewport widths: the sidebar is a drawer below this (ConditionalMain / Navbar isDesktop). */
export const RAILS_SIDEBAR_MIN_WIDTH = 768
/** The dock opens by default and reserves its pad only at or above this (AlertsChatDock matchMedia). */
export const RAILS_DOCK_MIN_WIDTH = 1024

/** Routes that render no navbar and no sidebar inset (ConditionalMain noNavbarRoutes). */
export const RAILS_NO_NAVBAR_ROUTES = ['/stream', '/labs/workspace'] as const

/**
 * Runs inline in <head>. Mirrors, value for value, what the components settle on:
 *   sidebar  = no-navbar route or ?embed=1 or width < 768 -> 0, else 72 when stored collapsed, else 368
 *   dock pad = 368 (+ html.alerts-chat-open) when the dock will restore OPEN at width >= 1024, else 0. "Will
 *              restore open" is chatDockInitialOpen (src/lib/chatDockDefault.ts) step for step: explicit choice,
 *              then CHAT_DOCK_DEFAULT_CLOSED, then the legacy key, then open on desktop. ?embed=1 never mounts it.
 * The flag is server env, so the layout builds the script per request; railsPrePaint.test.ts proves the script
 * and chatDockInitialOpen agree on every input combination. Any storage error leaves the vars unset (old behaviour).
 */
export function railsPrePaintScript(opts: { dockDefaultClosed: boolean }): string {
  return `(function(){try{var d=document.documentElement,w=window.innerWidth,p=location.pathname,`
    + `e=/(?:^|[?&])embed=1(?:&|$)/.test(location.search),`
    + `h=e||${JSON.stringify(RAILS_NO_NAVBAR_ROUTES)}.indexOf(p)>-1,s=0,c=0;`
    + `if(!h&&w>=${RAILS_SIDEBAR_MIN_WIDTH})s=localStorage.getItem('${SIDEBAR_COLLAPSED_STORAGE_KEY}')==='true'?${SIDEBAR_WIDTH_COLLAPSED_PX}:${SIDEBAR_WIDTH_EXPANDED_PX};`
    + `var x=localStorage.getItem('${CHAT_DOCK_CHOICE_KEY}'),l=localStorage.getItem('${CHAT_DOCK_LEGACY_KEY}'),`
    + `o=x==='1'?1:x==='0'?0:${opts.dockDefaultClosed ? 1 : 0}?0:l==='1'?1:l==='0'?0:w>=${RAILS_DOCK_MIN_WIDTH}?1:0;`
    + `if(!e&&o&&w>=${RAILS_DOCK_MIN_WIDTH}){c=${CHAT_DOCK_PAD_PX};d.classList.add('alerts-chat-open')}`
    + `d.style.setProperty('--sidebar-width',s+'px');d.style.setProperty('--alerts-chat-right-pad',c+'px')}catch(x){}})()`
}
